import React from 'react';
import { useTranslation } from '../../../i18n';

interface KPIOverviewCardProps {
  score: number;
  isPassed: boolean;
  passingScore?: number;
  readinessFrom?: number;
  readinessTo?: number;
  targetReadiness?: number;
  timeTaken?: string;
  allowedTime?: string;
  stagesCompleted?: number;
  totalStages?: number;
  evaluatedBy?: string;
  evaluatedAt?: string;
}

export const KPIOverviewCard: React.FC<KPIOverviewCardProps> = ({
  score = 0,
  isPassed = false,
  passingScore = 70,
  readinessFrom = 0,
  readinessTo = 0,
  targetReadiness = 85,
  timeTaken = '00:00',
  allowedTime = '15 min',
  stagesCompleted = 0,
  totalStages = 7,
  evaluatedBy = 'AI + coach review',
  evaluatedAt = 'Just now',
}) => {
  const { t } = useTranslation();
  const roundedScore = Math.round(score);
  const isBelowTarget = isPassed !== undefined && typeof isPassed === 'boolean' ? !isPassed : roundedScore < passingScore;

  const formattedAllowedTime = allowedTime
    ? typeof allowedTime === 'number'
      ? `${allowedTime} min`
      : !isNaN(Number(allowedTime))
      ? `${allowedTime} min`
      : allowedTime.startsWith('of ')
      ? allowedTime.substring(3)
      : allowedTime
    : '15 min';

  return (
    <div
      className="kpi-overview-card"
    >
      {/* Metric 1: Dynamic Circular Score Gauge */}
      <div className="kpi-overview-card__score" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '95px' }}>
        <div
          style={{
            width: '80px',
            height: '80px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          >
            {/* Background Track Ring */}
            <circle
              cx="40"
              cy="40"
              r="34"
              stroke="var(--line, #E2E8F0)"
              strokeWidth="5.5"
              fill="transparent"
            />
            {/* Dynamic Progress Arc (Starts at 12 o'clock top, fills clockwise) */}
            <circle
              cx="40"
              cy="40"
              r="34"
              stroke={isBelowTarget ? '#F59E0B' : '#10B981'}
              strokeWidth="5.5"
              strokeDasharray={213.63}
              strokeDashoffset={213.63 - (213.63 * Math.min(100, Math.max(0, roundedScore))) / 100}
              strokeLinecap="round"
              fill="transparent"
              transform="rotate(-90 40 40)"
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <span style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 800, color: 'var(--navy)', position: 'relative', zIndex: 1 }}>
            {roundedScore}%
          </span>
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600, marginTop: '6px' }}>
          {t('scorecard.overall_score_label', undefined, 'Overall score')}
        </span>
      </div>

      {/* Metric 2: Below Target / Target Status Banner */}
      <div className="kpi-overview-card__status" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: isBelowTarget ? '#FEF3C7' : '#D1FAE5',
              color: isBelowTarget ? '#92400E' : '#065F46',
              fontSize: '11.5px',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: '6px',
            }}
          >
            {isBelowTarget ? `✕ ${t('scorecard.below_target', undefined, 'Below Target')}` : `✓ ${t('scorecard.target_achieved', undefined, 'Target Achieved')}`}
          </span>
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)' }}>
            + 0 pts
          </span>
        </div>

        <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.45 }}>
          {t('scorecard.pass_mark_desc', { score: passingScore }, `Pass mark ${passingScore}%. Review the coaching feedback below and start your next attempt to achieve readiness.`)}
        </p>
      </div>

      {/* Metric 3: READINESS */}
      <div className="kpi-overview-card__metric" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          {t('scorecard.readiness_label', undefined, 'READINESS')}
        </span>
        <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--navy)' }}>
          {readinessFrom}% → {readinessTo}%
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          +0 · {t('scorecard.target_label', undefined, 'Target')} {targetReadiness}%
        </span>
      </div>

      {/* Metric 4: TIME TAKEN */}
      <div className="kpi-overview-card__metric" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          {t('scorecard.time_taken_label', undefined, 'TIME TAKEN')}
        </span>
        <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--navy)' }}>
          {timeTaken}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {formattedAllowedTime.startsWith('~') || formattedAllowedTime.includes('allowed') || formattedAllowedTime.startsWith('of ') ? formattedAllowedTime : t('scorecard.of_time', { time: formattedAllowedTime }, `of ${formattedAllowedTime}`)}
        </span>
      </div>

      {/* Metric 5: STAGES COMPLETED */}
      <div className="kpi-overview-card__metric" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          {t('scorecard.stages_completed_label', undefined, 'STAGES COMPLETED')}
        </span>
        <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--navy)' }}>
          {t('scorecard.stages_of', { current: stagesCompleted, total: totalStages }, `${stagesCompleted} of ${totalStages}`)}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {t('scorecard.all_stages_reached', undefined, 'All conversation stages reached')}
        </span>
      </div>

      {/* Metric 6: EVALUATED */}
      <div className="kpi-overview-card__metric" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          {t('scorecard.evaluated_label', undefined, 'EVALUATED')}
        </span>
        <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--navy)', whiteSpace: 'nowrap' }}>
          {evaluatedBy}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {evaluatedAt}
        </span>
      </div>
    </div>
  );
};
